import React, { useEffect, useState } from 'react';
import { Eye, EyeOff, Lock, ShieldCheck, Unlock } from 'lucide-react';
import BackOfficeMenu from '../BackOffice/components/BackOfficeMenu';
import { useProtectedDataLock } from '../context/SensitiveDataLockContext';
import './ProtectedSensitiveData.css';

const formatRemainingTime = (remainingMs) => {
  const totalSeconds = Math.max(0, Math.floor((remainingMs || 0) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

export const SensitiveUnlockCard = ({
  title = 'Contenu protégé',
  message = 'Veuillez saisir le mot de passe pour afficher ces informations sensibles.',
  className = '',
  pageMode = false
}) => {
  const { unlock, lastError, lastSuccess, clearMessages } = useProtectedDataLock();
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    clearMessages();
  }, [clearMessages]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const success = await unlock(password);
    if (success) {
      setPassword('');
    }
  };

  return (
    <section className={`sdl-unlock-card ${pageMode ? 'sdl-page-card' : ''} ${className}`}>
      <div className="sdl-lock-icon">
        <Lock size={26} />
      </div>
      <div className="sdl-card-text">
        <h2>{title}</h2>
        <p>{message}</p>
      </div>
      <form className="sdl-unlock-form" onSubmit={handleSubmit}>
        {/* Champ d'identifiant masqué pour l'accessibilité DOM et les gestionnaires de mots de passe */}
        <input
          type="text"
          name="username"
          autoComplete="username"
          style={{ display: 'none' }}
          readOnly
          value={localStorage.getItem('mail') || localStorage.getItem('userEmail') || 'admin'}
        />
        <label className="sdl-password-label">
          <span>Mot de passe</span>
          <div className="sdl-password-field">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="Saisir le mot de passe"
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword((value) => !value)}
              title={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </label>
        {lastError && <div className="sdl-message error">{lastError}</div>}
        {lastSuccess && <div className="sdl-message success">{lastSuccess}</div>}
        <button className="sdl-unlock-button" type="submit">
          <Unlock size={16} />
          Déverrouiller
        </button>
      </form>
    </section>
  );
};

export const ProtectedFinancialCard = ({
  children,
  title,
  message = 'Ces données financières sont masquées pendant la session verrouillée.',
  lockedClassName = '',
  actionCode = 'FINANCE_VIEW'
}) => {
  const { isUnlocked, checkActionProtected, isActionVisible } = useProtectedDataLock();

  // Si l'action est désactivée (enabled = false) ou masquée par la politique de sécurité -> NE RIEN AFFICHER
  if (!isActionVisible(actionCode)) return null;

  const isProtected = checkActionProtected(actionCode);

  // Si requirePassword = false dans la DB ou autorisation exceptionnelle active -> afficher directement sans popup
  if (!isProtected || isUnlocked) return <>{children}</>;

  return (
    <SensitiveUnlockCard
      title={title || 'Données financières protégées'}
      message={message}
      className={lockedClassName}
    />
  );
};

export const ProtectedPage = ({
  children,
  title,
  message = 'Cette interface contient des informations sensibles. Saisissez le mot de passe pour continuer.',
  onNavigate,
  onLogout,
  currentPage,
  actionCode
}) => {
  const { isUnlocked, checkActionProtected, isActionVisible } = useProtectedDataLock();

  // Mappage automatique des pages Backoffice vers leurs actionCodes respectifs
  const pageActionMap = {
    'deletion-settings': 'SETTINGS_UPDATE',
    'archived-items': 'ARCHIVE_RESTORE',
    'audit-logs': 'AUDIT_LOGS_VIEW'
  };
  const targetCode = actionCode || pageActionMap[currentPage];

  // Si l'action est désactivée par la politique de sécurité (enabled = false)
  if (targetCode && !isActionVisible(targetCode)) {
    return (
      <div className="sdl-protected-page-layout">
        <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />
        <main className="sdl-protected-page-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px' }}>
          <div style={{ backgroundColor: '#ffffff', borderRadius: '16px', padding: '32px 24px', textAlign: 'center', maxWidth: '480px', boxShadow: '0 10px 25px rgba(0,0,0,0.1)', border: '1px solid #fee2e2' }}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>🔒</div>
            <h3 style={{ color: '#991b1b', margin: '0 0 8px 0', fontSize: '1.25rem', fontWeight: 800 }}>Accès Désactivé</h3>
            <p style={{ color: '#991b1b', fontSize: '0.9rem', margin: 0, lineHeight: 1.5 }}>
              Cette fonctionnalité est actuellement désactivée par la politique de sécurité globale.
            </p>
          </div>
        </main>
      </div>
    );
  }

  // Si l'action n'exige pas de mot de passe (ou dérogation active), ne PAS demander de code
  const isPasswordRequired = targetCode ? checkActionProtected(targetCode) : true;
  const canDisplayContent = !isPasswordRequired || isUnlocked;

  return (
    <div className="sdl-protected-page-layout">
      <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />
      <main className="sdl-protected-page-content" style={{ display: canDisplayContent ? 'block' : 'flex', padding: canDisplayContent ? '20px' : '32px' }}>
        {canDisplayContent ? (
          children
        ) : (
          <SensitiveUnlockCard title={title} message={message} pageMode />
        )}
      </main>
    </div>
  );
};


export const ProtectedSessionBadge = () => {
  const { isUnlocked, remainingMs, lock } = useProtectedDataLock();
  if (!isUnlocked) return null;

  return (
    <button className="sdl-session-badge" type="button" onClick={lock} title="Verrouiller les contenus protégés">
      <ShieldCheck size={15} />
      <span>{formatRemainingTime(remainingMs)}</span>
      <strong>Verrouiller</strong>
    </button>
  );
};
